/**
 * Transforms appcore menus API response into the format expected by SideNav.
 * New API: { main: [{ action, display_order, icon_location, id, sub_menu, title, url }], docs: [...] }
 * Old format: [{ img, activeImg, route, title, key, subMenu? }] with optional href on sub-items for external links.
 * Uses API-provided `id` as `key` for stable identity.
 * Items whose url is listed in REACT_APP_HIDDEN_MENU_URLS are dropped (see utils/hiddenMenus).
 */

import { getHiddenMenuUrls, isMenuHidden } from 'utils/hiddenMenus';

const toIconPaths = (iconLocation) => {
  if (!iconLocation) return { img: '', activeImg: '' };
  const base = iconLocation.endsWith('.svg') ? iconLocation.replace(/\.svg$/, '') : iconLocation;
  return {
    img: `${base}.svg`,
    activeImg: `${base}-active.svg`,
  };
};

const transformSubMenuItem = (item) => {
  const { action, icon_location, id, title, url } = item;
  const icons = toIconPaths(icon_location);
  const key = id || title;

  if (action === 'redirect' && url) {
    return {
      ...icons,
      title,
      key,
      href: url,
    };
  }

  return {
    ...icons,
    route: url || '',
    title,
    key,
  };
};

const transformMainItem = (item, hiddenUrls) => {
  const { action, icon_location, id, sub_menu = [], title, url } = item;
  const icons = toIconPaths(icon_location);
  const key = id || title;

  const transformed = {
    ...icons,
    title,
    key,
  };

  if (action === 'open' && sub_menu.length > 0) {
    const visibleSubMenu = sub_menu.filter(
      (subItem) => !isMenuHidden(subItem?.url, hiddenUrls),
    );

    // An 'open' parent has no route of its own, so one that lost every child
    // to the filter would render as a heading that does nothing. Drop it.
    if (visibleSubMenu.length === 0) return null;

    transformed.subMenu = visibleSubMenu
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map(transformSubMenuItem);
  } else if (action === 'navigate' && url) {
    transformed.route = url;
  } else if (action === 'navigate') {
    transformed.route = '';
  }

  return transformed;
};

/**
 * Transforms a single product slice from the appcore menus response into SideNav format.
 * Works for any product (recur, flex, sync, etc.).
 * @param {{ main?: Array, docs?: Array }} product - A product slice from the menus response
 * @returns {Array} - Menu array in the format expected by SideNav
 */
export const transformAppCoreProductMenu = (product) => {
  if (!product || !Array.isArray(product.main)) return [];

  const hiddenUrls = getHiddenMenuUrls();

  return product.main
    .filter((item) => !isMenuHidden(item?.url, hiddenUrls))
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((item) => transformMainItem(item, hiddenUrls))
    .filter(Boolean);
};

export const API_RESPONSE_TO_APP_KEY = { flex: 'pulse' };

/**
 * Extracts and transforms docs arrays from the full menus API response,
 * keyed by application product key (recur, pulse, sync).
 * @param {Object} menusData - Full response from /menus (top-level keys are product names)
 * @returns {Object} - e.g. { recur: [{ title, url, action }], pulse: [...] }
 */
export const transformAppCoreDocsMenu = (menusData) => {
  if (!menusData) return {};

  return Object.keys(menusData).reduce((acc, apiKey) => {
    const productDocs = menusData?.[apiKey]?.docs;
    if (Array.isArray(productDocs) && productDocs.length > 0) {
      const appKey = API_RESPONSE_TO_APP_KEY[apiKey] || apiKey;
      acc[appKey] = productDocs
        .slice()
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((item) => ({
          title: item.title,
          url: item.url || '',
          action: item.action || 'redirect',
        }));
    }
    return acc;
  }, {});
};
